import { api } from './api';

export type SchrittId =
  | 'vollmacht_senden'
  | 'antrag_stellen'
  | 'bericht_schreiben'
  | 'bericht_senden'
  | 'bericht_gegenzeichnen'
  | 'vn_erstellen'
  | 'vn_senden'
  | 'bafa_hochladen';

export interface OffeneAufgabe {
  vorgangId: number;
  vorgangsnummer: string | null;
  customerId: number;
  kundeName: string | null;
  schritt: SchrittId;
  label: string;
  /** Passiert außerhalb des Systems (BAFA-Portal) → nur per Klick abhakbar. */
  nurManuell: boolean;
  /** Seit wann der Schritt fällig ist — das auslösende Dokument. */
  faelligSeit: string | null;
  /** Gesetzte Frist, sonst faelligSeit + 7 Tage. */
  frist: string | null;
  /** True → von Hand gesetzt, die Ableitung ist außer Kraft. */
  fristManuell: boolean;
  notiz: string | null;
  zustaendigUserId: number | null;
  zustaendigName: string | null;
}

/**
 * Was den Schritt verschwinden lässt — der Satz im aufgeklappten Bereich.
 *
 * Erklärt, warum niemand hier abhaken muss: Die Aufgabe endet mit dem Dokument, nicht mit
 * einem Klick. Nur der BAFA-Upload passiert außerhalb und braucht die Hand.
 */
export const SCHRITT_HINWEIS: Record<SchrittId, string> = {
  vollmacht_senden:
    'Verschwindet, sobald die unterschriebene Vollmacht beim Kunden in der Ablage liegt.',
  antrag_stellen:
    'Verschwindet, sobald der Vorgang eine Vorgangsnummer trägt oder auf „gestellt" steht.',
  bericht_schreiben: 'Verschwindet, sobald eine Bericht-PDF am Vorgang liegt.',
  bericht_senden: 'Verschwindet, sobald der Bericht zur Unterschrift verschickt wurde.',
  bericht_gegenzeichnen:
    'Der Kunde hat unterschrieben. Verschwindet, sobald auch Ihre Unterschrift hochgeladen ist — den vollständigen Bericht setzt das System daraus zusammen.',
  vn_erstellen: 'Verschwindet, sobald ein Verwendungsnachweis am Vorgang liegt.',
  vn_senden: 'Verschwindet, sobald der Verwendungsnachweis zur Unterschrift verschickt wurde.',
  bafa_hochladen:
    'Das Hochladen passiert im BAFA-Portal — davon bekommt dieses System nichts mit. Deshalb hier von Hand abhaken.',
};

export const listAufgaben = () => api.get<OffeneAufgabe[]>('/api/aufgaben');

export const aufgabeAbhaken = (vorgangId: number, schritt: SchrittId) =>
  api.post<{ ok: true }>(`/api/aufgaben/${vorgangId}/${schritt}/erledigt`, {});

/** Frist, Notiz, Zuständigkeit. `null` je Feld stellt die Ableitung wieder her. */
export const aufgabeZusatz = (
  vorgangId: number,
  schritt: SchrittId,
  patch: { frist?: string | null; notiz?: string | null; zustaendigUserId?: number | null },
) => api.patch<{ ok: true }>(`/api/aufgaben/${vorgangId}/${schritt}`, patch);

/**
 * Reihenfolge der Kette — je später der Schritt, desto weiter vorne steht er.
 *
 * Ein Vorgang kurz vor der Auszahlung ist wertvoller als einer, bei dem der Bericht erst
 * geschrieben werden muss: Dort hängt Geld am Abschluss, hier ist noch alles offen.
 */
const RANG: Record<SchrittId, number> = {
  bafa_hochladen: 0,
  vn_senden: 1,
  vn_erstellen: 2,
  bericht_gegenzeichnen: 3,
  bericht_senden: 4,
  bericht_schreiben: 5,
  antrag_stellen: 6,
  vollmacht_senden: 7,
};

export function sortAufgaben<T extends { schritt: SchrittId; kundeName: string | null }>(list: T[]): T[] {
  return [...list].sort((a, b) => {
    const r = RANG[a.schritt] - RANG[b.schritt];
    if (r !== 0) return r;
    return (a.kundeName ?? '').localeCompare(b.kundeName ?? '', 'de');
  });
}
